import { useAuth, hasPermission } from '@/contexts/AuthContext'
import { useAdminUsers } from '@/hooks/useAdminUsers'
import { useToast } from '@/hooks/useToast'
import { supabase, toError } from '@/lib/supabase'
import { formatDateTime } from '@/lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import { Users, Loader2, Shield, ShieldCheck, Trash2, Search } from 'lucide-react'
import type { AdminRole } from '@/types/database'

const ROLES: AdminRole[] = ['super_admin', 'admin', 'viewer', 'investor']

const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  viewer: 'Viewer',
  investor: 'Investor',
}

function getRoleBadgeColor(role: AdminRole) {
  switch (role) {
    case 'super_admin':
      return 'bg-brand-fg/20 text-brand-fg'
    case 'admin':
      return 'bg-blue-500/20 text-blue-400'
    case 'investor':
      return 'bg-emerald-500/20 text-emerald-400'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

export function UsersPage() {
  const { user, adminUser } = useAuth()
  const { toast } = useToast()
  const { data: users, isLoading, error, updateRole, deleteUser } = useAdminUsers()

  const isSuperAdmin = hasPermission(adminUser?.role, 'super_admin') && adminUser?.role === 'super_admin'
  const [searchQuery, setSearchQuery] = useState('')

  const handleRoleChange = async (userId: string, email: string, newRole: AdminRole) => {
    try {
      await updateRole.mutateAsync({ userId, role: newRole })

      // Audit log
      await supabase.rpc('log_admin_audit_event', {
        p_action: 'user_role_updated',
        p_details: { target_user_id: userId, target_email: email, new_role: newRole },
        p_user_agent: navigator.userAgent,
      })

      toast({
        title: 'Role Updated',
        description: `${email} is now ${ROLE_LABELS[newRole]}`,
      })
    } catch (e) {
      toast({
        title: 'Error',
        description: toError(e, 'Failed to update role').message,
        variant: 'destructive',
      })
    }
  }

  const handleDelete = async (userId: string, email: string) => {
    if (userId === user?.id) {
      toast({
        title: 'Cannot remove yourself',
        description: 'Have another super admin remove your account.',
        variant: 'destructive',
      })
      return
    }

    if (!window.confirm(`Remove ${email} from the platform? This cannot be undone.`)) return

    try {
      await deleteUser.mutateAsync(userId)

      // Audit log
      await supabase.rpc('log_admin_audit_event', {
        p_action: 'user_deleted',
        p_details: { target_user_id: userId, target_email: email },
        p_user_agent: navigator.userAgent,
      })

      toast({
        title: 'User Removed',
        description: `${email} has been removed`,
      })
    } catch (e) {
      toast({
        title: 'Error',
        description: toError(e, 'Failed to remove user').message,
        variant: 'destructive',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Users</h2>
          <p className="text-sm sm:text-base text-muted-foreground">Manage platform users</p>
        </div>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground p-4 sm:p-6">
            <Shield className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm sm:text-base">{error instanceof Error ? error.message : 'Failed to load users'}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!users || users.length === 0) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Users</h2>
          <p className="text-sm sm:text-base text-muted-foreground">Manage platform users</p>
        </div>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground p-4 sm:p-6">
            <Users className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm sm:text-base">No users found</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">Users</h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Manage platform users
          <span className="ml-2 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {users.length}
          </span>
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-fg"
        />
      </div>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <ShieldCheck className="h-5 w-5" />
            All Users
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-border">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Role</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">MFA</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Created</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Last Sign In</th>
                  {isSuperAdmin && (
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {users.filter((u) => !searchQuery || u.email.toLowerCase().includes(searchQuery.toLowerCase())).map((u) => {
                  const isCurrentUser = u.id === user?.id
                  return (
                    <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <span className="font-medium">{u.email}</span>
                        {isCurrentUser && (
                          <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isSuperAdmin && !isCurrentUser ? (
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.id, u.email, e.target.value as AdminRole)}
                            disabled={updateRole.isPending}
                            className="h-8 px-2 rounded-md border border-input bg-background text-sm"
                          >
                            {ROLES.map((r) => (
                              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                            ))}
                          </select>
                        ) : (
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                            {ROLE_LABELS[u.role]}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.mfa_enabled ? (
                          <span className="text-emerald-400 text-xs font-medium">Enabled</span>
                        ) : (
                          <span className="text-muted-foreground text-xs">Disabled</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                        {formatDateTime(u.created_at)}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">
                        {u.last_login ? formatDateTime(u.last_login) : 'Never'}
                      </td>
                      {isSuperAdmin && (
                        <td className="px-4 py-3 text-right">
                          {!isCurrentUser && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(u.id, u.email)}
                              disabled={deleteUser.isPending}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
